import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

const FOCUSABLE = 'a[href], button, input, select, textarea, [contenteditable="true"], [tabindex]';

/** Marks the elements this extension took out of the Tab order; its value is their own tabindex */
const UNTABBABLE_ATTRIBUTE = "data-untabbable";

/** The transaction meta that turns the content's links and buttons back on or off */
export const contentTabbableKey = new PluginKey<boolean>("contentTabbable");

/**
 * Takes a read-only editor's links and buttons out of the Tab order while `tabbable` is off, like
 * a chat message that isn't entered: the message list keeps one Tab stop per list, not per link.
 *
 * Read-only only: ProseMirror reads DOM changes back into the document only in an editable view,
 * where it would re-render a link whose `tabindex` changed.
 */
export const ContentTabbable = Extension.create<{ tabbable: boolean }>({
  name: "contentTabbable",

  addOptions() {
    return { tabbable: true };
  },

  addProseMirrorPlugins() {
    const { tabbable: initiallyTabbable } = this.options;

    return [
      new Plugin<boolean>({
        key: contentTabbableKey,
        state: {
          init: () => initiallyTabbable,
          apply: (tr, tabbable) =>
            (tr.getMeta(contentTabbableKey) as boolean | undefined) ?? tabbable,
        },
        view(view) {
          const sync = () => {
            const tabbable = contentTabbableKey.getState(view.state);
            view.dom.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((element) => {
              if (tabbable) {
                const original = element.getAttribute(UNTABBABLE_ATTRIBUTE);
                if (original === null) return;
                element.removeAttribute(UNTABBABLE_ATTRIBUTE);
                if (original) element.setAttribute("tabindex", original);
                else element.removeAttribute("tabindex");
              } else if (element.getAttribute("tabindex") !== "-1") {
                element.setAttribute(UNTABBABLE_ATTRIBUTE, element.getAttribute("tabindex") ?? "");
                element.setAttribute("tabindex", "-1");
              }
            });
          };

          // Node views (React ones included) may render their buttons after an update
          const observer = new MutationObserver(sync);
          observer.observe(view.dom, { childList: true, subtree: true });
          sync();

          return { update: sync, destroy: () => observer.disconnect() };
        },
      }),
    ];
  },
});
